import { Controller } from "stimulus";
import SlimSelect from "slim-select";

export default class extends Controller {
  static targets = ["selectElement"];
  timeout = null; // used for debounce
  isSaving = false; // prevent multiple simultaneous saves
  previousSelections = new Map(); // track previous selections per select element

  connect() {
    // Only initialize slim-select for lookup field dropdowns within this controller's scope
    this.initializeLookupFieldSlimSelect();
    this.setupFormSubmissionHandling();
    this.initializePreviousSelections();
  }

  // Initialize SlimSelect only for lookup field dropdowns
  // This prevents interfering with other dropdowns like liquid tag dropdowns
  initializeLookupFieldSlimSelect() {
    // Only find selects within lookup field blocks (elements with look-up controller)
    const lookupFieldElements = document.querySelectorAll('[data-controller*="look-up"]');
    
    lookupFieldElements.forEach((lookupFieldElement) => {
      const selectElements = lookupFieldElement.querySelectorAll("select.slim-select");
      
      selectElements.forEach((selectElement) => {
        // Skip if already initialized
        if (selectElement.slimSelectInstance) {
          return;
        }
        
        // Skip if marked as already initialized by other initialization code
        if (selectElement.hasAttribute('data-slim-select-initialized')) {
          return;
        }
        
        // Only initialize lookup field selects (not liquid dropdowns)
        // Liquid dropdowns should be initialized by create/update.js.erb scripts
        const isLiquidDropdown = selectElement.closest('[data-controller*="liquid-dropdown"]');
        if (!isLiquidDropdown) {
          this.initializeSlimSelectForElement(selectElement);
        }
      });
    });
  }

  initializeSlimSelectForElement(selectElement) {
    if (!selectElement) {
      console.error("Select element not found");
      return;
    }

    // Check if this select is inside a liquid-dropdown controller
    // Use closest with selector to properly detect liquid-dropdown even if multiple controllers are present
    const isLiquidDropdown = selectElement.closest('[data-controller*="liquid-dropdown"]');
    const close = !!isLiquidDropdown;

    const isFormField = selectElement.closest('[data-form="true"]');

    selectElement.lookupController = this;

    selectElement.slimSelectInstance = new SlimSelect({
      select: selectElement,
      settings: {
        closeOnSelect: close,
        allowDeselect: true,
      },
      events: isFormField ? {} : { 
        afterChange: (newValues) => {
          const isProgrammatic = selectElement._isProgrammaticChange;
          
          selectElement._isProgrammaticChange = false;
          
          if (!isProgrammatic) {
            this.save(selectElement);
          } else {
            const selectedValues = Array.isArray(newValues) ? newValues : (newValues ? [newValues] : []);
            const currentSelection = selectedValues.sort().join(',');
            this.previousSelections.set(selectElement, currentSelection);
          }
        }
      },
    });

    const selectedValues = Array.from(selectElement.selectedOptions).map(option => option.value);
    const currentSelection = selectedValues.sort().join(',');
    this.previousSelections.set(selectElement, currentSelection);
  }

  setupFormSubmissionHandling() {
    // Add global form submission handler to sync slim-select values
    document.addEventListener('submit', (event) => {
      this.syncAllSlimSelectValues(event.target);
    });
  }

  initializePreviousSelections() {
    // Initialize previous selections for all lookup fields on the page
    const lookupFields = document.querySelectorAll('[data-controller*="look-up"] select.slim-select');
    lookupFields.forEach(selectElement => {
      const selectedValues = Array.from(selectElement.selectedOptions).map(option => option.value);
      const currentSelection = selectedValues.sort().join(',');
      this.previousSelections.set(selectElement, currentSelection);
      console.log('🔍 Initialized previous selection for lookup field:', {
        selectElement,
        selectedValues,
        currentSelection
      });
    });
  }





  syncAllSlimSelectValues(form) {
    // Find all slim-select elements in the form and sync their values
    const selectElements = form.querySelectorAll('select.slim-select');
    selectElements.forEach(select => {
      this.syncSlimSelectValue(select);
    });
  }

  syncSlimSelectValue(selectElement) {
    if (selectElement.slimSelectInstance) {
      // Get the selected value from slim-select
      const selectedValue = selectElement.slimSelectInstance.getSelected();
      // Sync it back to the original select element
      if (selectedValue && selectedValue.length > 0) {
        selectElement.value = selectedValue[0];
      } else {
        selectElement.value = "";
      }
      console.log(`Synced slim-select value for ${selectElement.name}: ${selectElement.value}`);
    }
  }

  save(selectElement) {
    const selectedValues = Array.from(selectElement.selectedOptions).map(option => option.value);
    const currentSelection = selectedValues.sort().join(',');
    const previousSelection = this.previousSelections.get(selectElement) || '';
    
    console.log('🔍 Lookup field save triggered:', {
      selectedValues,
      currentSelection,
      previousSelection,
      changed: currentSelection !== previousSelection,
      selectElement: selectElement
    });
    
    // Only save if the selection actually changed
    if (currentSelection === previousSelection) {
      console.log('Selection unchanged, skipping save');
      return;
    }
    
    console.log('🔍 Selection changed, proceeding with save');
    
    // Update the previous selection
    this.previousSelections.set(selectElement, currentSelection);
    
    // Clear any previous scheduled save
    clearTimeout(this.timeout);

    // Set a new timeout to delay the save (same as rich text fields)
    this.timeout = setTimeout(() => {
      console.log('🔍 Executing performSave - BACKEND ONLY, NO UI CHANGES');
      this.performSave(selectElement);
    }, 100); // Quick save for lookup fields
  }


  performSave(selectElement) {
    console.log('🔍 performSave called for:', selectElement);
    
    // Prevent multiple simultaneous saves
    if (this.isSaving) {
      console.log("Save already in progress, skipping...");
      return;
    }

    this.isSaving = true;

    const selectedValues = Array.from(selectElement.selectedOptions).map(option => option.value);
    
    // Find the closest parent element with data attributes
    const parentElement = selectElement.closest('[data-controller*="look-up"]');
    if (!parentElement) {
      console.error('Could not find parent element with data attributes for:', selectElement);
      this.isSaving = false;
      return;
    }

    const { blockType } = parentElement.dataset;

    console.log('🔍 Performing save:', {
      selectedValues,
      blockType,
      endpoint: this.getEndpoint(blockType, selectElement),
      parentElementDataset: parentElement.dataset
    });

    if (!blockType) {
      console.log('🔍 No blockType found, skipping save');
      this.isSaving = false;
      return;
    }

    const endpoint = this.getEndpoint(blockType, selectElement);
    if (!endpoint) {
      console.error("Invalid block type or missing dataset attributes");
      this.isSaving = false;
      return;
    }

    console.log('🔍 Calling updateContent with:', { endpoint, selectedValues });
    this.updateContent(endpoint, selectedValues, parentElement);
  }


  getEndpoint(blockType, selectElement) {
    // Find the closest parent element with data attributes
    const parentElement = selectElement.closest('[data-controller*="look-up"]');
    if (!parentElement) {
      console.error('Could not find parent element with data attributes for:', selectElement);
      return null;
    }

    const { accountId } = parentElement.dataset;

    if (blockType === "step") {
      const { processTemplateId, stepTemplateId, blockId } = parentElement.dataset;
      return `/${accountId}/process_templates/${processTemplateId}/step_templates/${stepTemplateId}/step_blocks/${blockId}/update_content`;
    }

    if (blockType === "task") {
      const { projectId, taskListId, taskId, blockId } = parentElement.dataset;
      return `/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/${blockId}/update_content`;
    }

    return null;
  }

  updateContent(url, selectedValues, parentElement) {
    const { blockType } = parentElement.dataset;
    const requestBody = {
      [`${blockType}_block`]: { project_ids: selectedValues },
      skip_broadcast: true // Prevent ActionCable broadcast to avoid flashing
    };
    
    console.log('🔍 Sending update to backend:', {
      url,
      requestBody,
      selectedValues,
      blockType
    });
    
    fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify(requestBody)
    })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then(data => {
        if (data.success) {
          console.log("Selected values updated:", data.project_ids);
          
          if (blockType === "task") {
            const { accountId, projectId, taskListId, taskId, blockId } = parentElement.dataset;
            
            if (accountId && projectId && taskListId && taskId && blockId) {
              const event = new CustomEvent('taskBlockUpdated', {
                detail: {
                  block_id: blockId,
                  account_id: accountId,
                  project_id: projectId,
                  task_list_id: taskListId,
                  task_id: taskId,
                  action: 'update_value'
                }
              });
              document.dispatchEvent(event);
              
              const renderUrl = `/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/${blockId}/render_referencing_task_blocks`;
              
              if (typeof Rails !== 'undefined' && Rails.ajax) {
                Rails.ajax({
                  url: renderUrl,
                  type: 'GET',
                  dataType: "script"
                });
              } else {
                fetch(renderUrl, {
                  method: "GET",
                  headers: {
                    "Accept": "text/javascript",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
                  }
                })
                  .then(response => {
                    if (response.ok) {
                      return response.text();
                    }
                    throw new Error(`HTTP error! status: ${response.status}`);
                  })
                  .then(script => {
                    eval(script);
                  })
                  .catch(error => {});
              }
            }
          }
        }
      })
      .catch(error => console.error("Error updating selected values:", error))
      .finally(() => {
        this.isSaving = false;
      });
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.previousSelections.clear();
  }
}
